type TrackingEvent = {
	eventName: string;
	payload?: Record<string, unknown>;
};

type EventOrigin = 'scenario_page' | 'agent_page' | 'unknown_page';

const SUPPORTED_PAGES = {
	agents: 'agent_page',
	scenarios: 'scenario_page',
} as const;

type PageKey = keyof typeof SUPPORTED_PAGES;

const getRouteDetails = () => {
	const matches = window.location.pathname.match(/\/\d+\/(\w+)/);
	if (!matches) return {};

	const [, pageKey] = matches;
	if (!(pageKey in SUPPORTED_PAGES)) return {};

	return {
		page: SUPPORTED_PAGES[pageKey as PageKey],
	};
};

const getEventOrigin = (): EventOrigin => {
	const { page } = getRouteDetails();
	return page ?? 'unknown_page';
};

export const trackAnyEvent: <T extends TrackingEvent>(t: T) => Promise<void> = async (event) => {
	if (!imt.apiurl) {
		console.warn('Tracking is disabled because `apiurl` is not set.');
		return;
	}

	const url = new URL(`${imt.apiurl}/trace`, window.location.origin);

	await fetch(url, {
		body: JSON.stringify(event),
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

type FormanEventMeta = {
	team_id: number;
	user_id: number;
	organization_id: number;
	app_name?: string;
	app_version?: string;
	module_name?: string;
	form_id?: string;
};

const trackFormanEvent = trackAnyEvent<TrackingEvent & { payload: FormanEventMeta }>;

export const createFormanTracking = (meta: FormanEventMeta) => ({
	/**
	 * Tracks when user clicks on add module
	 */
	trackModuleAdd: () =>
		trackFormanEvent({
			eventName: 'forman_ai_agent_module_add_clicked',
			payload: {
				...{ team_id: meta.team_id, user_id: meta.user_id, organization_id: meta.organization_id },
				event_origin: getEventOrigin(),
			},
		}),

	/**
	 * Tracks when user selects item in ai agents tool dropdown
	 */
	trackAiAgentDropdownSelect: ({ dropdownItem }: { dropdownItem: string }) =>
		trackFormanEvent({
			eventName: 'ai_agent_tool_dropdown_type_selected',
			payload: {
				...{ team_id: meta.team_id, user_id: meta.user_id, organization_id: meta.organization_id },
				event_origin: getEventOrigin(),
				dropdown_item: dropdownItem,
			},
		}),

	/**
	 * Tracks when user clicks on one of scenario list actions
	 */
	trackScenarioListAction: ({ entityType, actionType }: { entityType: string; actionType: string }) =>
		trackFormanEvent({
			eventName: 'forman_scenario_list_action_clicked',
			payload: {
				...{ team_id: meta.team_id, user_id: meta.user_id, organization_id: meta.organization_id },
				event_origin: getEventOrigin(),
				action_type: actionType,
				entity_type: entityType,
			},
		}),

	/**
	 * Tracks when user clicks on add tools of ai agent
	 */
	trackAiAgentAddTools: () =>
		trackFormanEvent({
			eventName: 'ai_agent_add_tools_clicked',
			payload: {
				...{ team_id: meta.team_id, user_id: meta.user_id, organization_id: meta.organization_id },
				event_origin: getEventOrigin(),
			},
		}),

	/**
	 * Tracks when user collapses/expanded the individual inputs fields
	 */
	trackCollapseInputField: ({ isCollapsed }: { isCollapsed: boolean }) =>
		trackFormanEvent({
			eventName: 'forman_toggle_input',
			payload: {
				...meta,
				is_collapsed: isCollapsed,
			},
		}),

	/**
	 * Tracks when user collapses/expandes all the inputs fields at once
	 * These actions are expected to be called from the UiPanelMenuComponent in web-zone
	 *
	 * @see {@link https://github.com/integromat/imt-web-zone/blob/master/libs/shared/ui-panel-menu/src/lib/ui-panel-menu.component.ts}
	 */
	trackCollapseAllInputFields: ({ isCollapsed }: { isCollapsed: boolean }) =>
		trackFormanEvent({
			eventName: 'forman_toggle_all_inputs',
			payload: {
				...meta,
				is_collapsed: isCollapsed,
			},
		}),

	/**
	 * Tracks when user refreshes the entire form
	 * This action is expected to be called from the UiPanelMenuComponent in web-zone
	 *
	 * @see {@link https://github.com/integromat/imt-web-zone/blob/master/libs/shared/ui-panel-menu/src/lib/ui-panel-menu.component.ts}
	 */
	trackRefreshForm: () =>
		trackFormanEvent({
			eventName: 'forman_refresh_form',
			payload: {
				...meta,
			},
		}),

	/**
	 * Tracks when user displays the type of inputs fields by hovering the input's label
	 */
	trackDisplayInputFieldTooltip: () =>
		trackFormanEvent({
			eventName: 'forman_display_input_field_tooltip',
			payload: {
				...meta,
			},
		}),

	/**
	 * Tracks when auto complete is opened.
	 */
	trackAutoCompleteOpen: () =>
		trackFormanEvent({
			eventName: 'forman_auto_complete_open',
			payload: {
				...meta,
			},
		}),

	/**
	 * Tracks when auto complete fields are added to the input. We only track the name/type of the variable (ex: mapAI/function).
	 *
	 * There are different sources that we are tracking for pills:
	 *  - auto-complete: when user selects a variable from the auto-complete suggestions
	 *  - drag: when user drags a variable from the hint panel or from another forman field
	 *  - click: when user clicks on a variable from the hint panel
	 *
	 * There are probably more sources (like copy-paste), but we are not tracking them for now.
	 */
	trackPillInserted: ({
		variableName,
		variableType,
		source,
		isBundle = false,
	}: {
		variableName: string;
		variableType: string;
		source: 'auto-complete' | 'drag' | 'click';
		isBundle?: boolean;
	}) =>
		trackFormanEvent({
			eventName: 'forman_pill_inserted',
			payload: {
				...meta,
				variable_name: variableName,
				variable_type: variableType,
				source,
				is_bundle: isBundle,
			},
		}),
});
